import {Card, Icon, DeprecatedCreativeModal as Modal, tokensForThemeProvider} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';
import {REPORT_TYPE, ReportType} from '../../../../../constants';
import {PERMISSION_TYPE} from '../../../../../Permissions';
import {hasSomePermission} from '../../../../shared/util/PermissionsUtil';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';
import {canUseAgentChat} from '../../../../../components/AgentDrawer/agentChatGate';
import {canOpenAuditLogReport, newAuditLogReportPayload} from '../../../../../components/ai-report-builder/auditLogReport';
import {endOfMonth, format, startOfMonth} from 'date-fns';
import {useAgentDrawerContext} from '../../../../shared/contexts/AgentDrawerContext';

const sysTokens = tokensForThemeProvider.sky.sys;

const GridContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 24px;
`;

// Card (from the design system) hardcodes a blue-fill hover whenever it's given an onClick, with
// no prop to opt out. Rather than fight that from outside, these tiles never pass onClick to
// Card itself — Card is then purely presentational (no hover, no cursor) — and the click handler
// and the modal's own drop-shadow hover live on this wrapper instead.
//
// `display: flex` (with the default `align-items: stretch`) is required here: this div, not Card,
// is now the grid item, so it alone receives the grid's row-stretch. Without a flex/stretch
// relationship to its child, Card would stay at its own content height while this wrapper filled
// the full (tallest-sibling) row height, leaving the hover shadow and click target hanging below
// the visibly bordered card whenever a neighbouring tile's description wraps to more lines.
const HoverShadowTile = styled.div`
	display: flex;
	cursor: pointer;
	border-radius: ${sysTokens.size.borderradius['5']};
	transition: box-shadow 150ms ease;

	&:hover {
		box-shadow: ${sysTokens.elevation.shadow['1']};
	}
`;

// The report-builder CTA isn't a report-type tile — it's a permanently accent-filled banner (see
// the Figma design), so it's built as its own element rather than forced into Card's shape.
const ReportBuilderTile = styled.div`
	grid-column: 1 / -1;
	display: flex;
	align-items: center;
	gap: ${sysTokens.spacing['300']};
	padding: ${sysTokens.spacing['400']};
	border-radius: ${sysTokens.size.borderradius['5']};
	background: linear-gradient(135deg, ${sysTokens.color.brand.primary['2']} 0%, ${sysTokens.color.brand.primary['1']} 100%);
	color: ${sysTokens.color.text.content.ondark};
	cursor: pointer;
	transition: box-shadow 150ms ease;

	&:hover {
		box-shadow: ${sysTokens.elevation.shadow['1']};
	}
`;

const ReportBuilderTileTitle = styled.div`
	display: flex;
	align-items: center;
	gap: ${sysTokens.spacing['200']};
	${sysTokens.font.subheading['2']}
	color: ${sysTokens.color.text.content.ondark};
`;

const ReportBuilderTileBody = styled.div`
	${sysTokens.font.detail['1']}
	color: ${sysTokens.color.text.content.ondark};
`;

const CreateReportModal = ({createReport, viewer, closeModal}) => {
	const {company, profiles} = viewer;
	const history = useHistory();
	const {formatMessage} = useIntl();
	const {openDrawerWithIntro} = useAgentDrawerContext();

	const hasFinancialAccess = hasSomePermission([
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION,
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE,
	]);

	// Both AI capability flags plus the same Nova exclusion (client users, external
	// collaborators) already applied to the nav trigger and the drawer itself — see
	// agentChatGate.ts. Deliberately not canOpenReportBuilder(): that gate also requires
	// INSIGHTS_READ, which nothing in this ticket asks for.
	const canUseReportBuilderCta = hasFeatureFlag('ai_report_builder') && canUseAgentChat(profiles);

	// Its own flag and its own permissions, checked by the same gate the Reports list and the share
	// route use. Deliberately independent of `canUseReportBuilderCta` above: the audit log renders on
	// the builder's surface but is not an AI feature, and a company entitled to one is not thereby
	// entitled to the other.
	const canCreateAuditLogReport = canOpenAuditLogReport();

	function handleCreateReport(type: ReportType) {
		return createReport(type, company, history, closeModal);
	}

	function handleOpenReportBuilderCta() {
		closeModal();
		openDrawerWithIntro('reportBuilder');
	}

	/**
	 * Opens a fresh audit log report on the builder's surface.
	 *
	 * Unlike its four neighbours this does NOT go through `createReport`: that path creates a stored
	 * report of a standard type and opens the page built for it, and this type has no such page. The
	 * report is instead handed to the builder as a draft in router state, the way the agent hands one
	 * over, and only becomes a stored report when the reader saves it. So a reader who opens this and
	 * changes their mind leaves nothing behind.
	 *
	 * The period defaults to the current month, matching what `buildSaveReportAsInput` falls back to
	 * for a definition that names no range -- so the window the reader first sees is the window a
	 * save would have recorded anyway.
	 */
	function handleCreateAuditLogReport() {
		const today = new Date();
		const payload = newAuditLogReportPayload(
			format(startOfMonth(today), 'yyyy-MM-dd'),
			format(endOfMonth(today), 'yyyy-MM-dd'),
			formatMessage({id: 'report.time_approval_audit_log.title'})
		);
		closeModal();
		history.push('/report-builder', {initialConfig: payload});
	}

	return (
		<Modal title="Create a New Report" size="xl" withoutButtons closeModal={closeModal} replicateDesignSystem>
			<GridContainer>
				<HoverShadowTile
					data-cy={'create-report-card-time-reg'}
					onClick={() => handleCreateReport(REPORT_TYPE.TIME_REG)}
				>
					<Card>
						<Card.Title>
							<Icon icon={'clock'} />
							{formatMessage({id: 'report.time_registration.title'})}
						</Card.Title>
						<Card.Body>{formatMessage({id: 'report.time_registration.description'})}</Card.Body>
					</Card>
				</HoverShadowTile>
				{hasFinancialAccess ? (
					<HoverShadowTile
						data-cy={'create-report-card-ppr'}
						onClick={() => handleCreateReport(REPORT_TYPE.PROJECT_PORTFOLIO_REPORT)}
					>
						<Card>
							<Card.Title>
								<Icon icon={'briefcase'} />
								{formatMessage({id: 'report.project_portfolio.title'})}
							</Card.Title>
							<Card.Body>{formatMessage({id: 'report.project_portfolio.description'})}</Card.Body>
						</Card>
					</HoverShadowTile>
				) : null}
				<HoverShadowTile
					data-cy={'create-report-card-ut'}
					onClick={() => handleCreateReport(REPORT_TYPE.UTILIZATION_REPORT)}
				>
					<Card>
						<Card.Title>
							<Icon icon={'gauge'} />
							{formatMessage({id: 'report.utilization.title'})}
						</Card.Title>
						<Card.Body>{formatMessage({id: 'report.utilization.description'})}</Card.Body>
					</Card>
				</HoverShadowTile>
				<HoverShadowTile
					data-cy={'create-report-card-task'}
					onClick={() => handleCreateReport(REPORT_TYPE.TASK_REPORT)}
				>
					<Card>
						<Card.Title>
							<Icon icon={'task'} />
							{formatMessage({id: 'report.task.title'})}
						</Card.Title>
						<Card.Body>{formatMessage({id: 'report.task.description'})}</Card.Body>
					</Card>
				</HoverShadowTile>
				{canCreateAuditLogReport ? (
					<HoverShadowTile data-cy={'create-report-card-audit-log'} onClick={handleCreateAuditLogReport}>
						<Card>
							<Card.Title>
								<Icon icon={'timeEntry'} />
								{formatMessage({id: 'report.time_approval_audit_log.title'})}
							</Card.Title>
							<Card.Body>{formatMessage({id: 'report.time_approval_audit_log.description'})}</Card.Body>
						</Card>
					</HoverShadowTile>
				) : null}
				{canUseReportBuilderCta ? (
					<ReportBuilderTile data-cy={'create-report-card-ai-builder'} onClick={handleOpenReportBuilderCta}>
						<Icon icon={'sparkle'} customColor={sysTokens.color.iconfill.ondark} />
						<div>
							<ReportBuilderTileTitle>{formatMessage({id: 'report.ai_builder.title'})}</ReportBuilderTileTitle>
							<ReportBuilderTileBody>
								{formatMessage({id: 'report.ai_builder.description'})}
							</ReportBuilderTileBody>
						</div>
					</ReportBuilderTile>
				) : null}
			</GridContainer>
		</Modal>
	);
};

export const CreateReportModalQuery = graphql`
	query CreateReportModalQuery {
		viewer {
			actualPersonId
			component(name: "create_report_modal")
			...CreateReportModal_viewer
		}
	}
`;

export default createFragmentContainer(CreateReportModal, {
	viewer: graphql`
		fragment CreateReportModal_viewer on Viewer {
			company {
				isUsingProjectAllocation
				isUsingSchedulingPlanMode
				isUsingMixedAllocation
				sageIntacctConnectionEnabled
				sageIntacctTimeRegsFromForecast
			}
			profiles(first: 1) {
				edges {
					node {
						seat
					}
				}
			}
		}
	`,
});
